/**
 * =============================================================================
 * Página: ReferralReferralsAdmin
 * Versão: 1.1.0
 *
 * Descrição:
 *   Listagem administrativa centralizada de **Indicações** (leads/funil)
 *   capturadas pelos afiliados do tenant. Padronizada com os helpers
 *   `list-page` (paridade visual com Afiliados / Comissões / Cobranças).
 *
 *   Recursos:
 *     - 4 KPIs (Total / Pendentes / Convertidas / Receita)
 *     - Filtros rápidos: busca textual, programa, campanha, status
 *     - Filtros avançados em Sheet "Mais": afiliado, faixa de criação
 *     - Tabela full-bleed com colunas obrigatórias (Lead, Origem, Status,
 *       Valor, Nº Negócio, Nº Contato, Criado em, Convertido em, Ações)
 *     - Detail Sheet com timeline completo
 *     - Reprocessamento manual (quando contato/negócio não foram criados)
 *     - Modal premium do negócio vinculado (clique na coluna "Nº Negócio")
 *
 * RELEASE:
 *   2026-04-26 v1.0.0 - Criação inicial.
 *   2026-04-28 v1.1.0 - Coluna "Nº Negócio" abre o DealDetailsModalPremium
 *                       (mesmo modal usado em CrmActivities), reaproveitando
 *                       padrão consolidado do projeto.
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Inbox,
  Search,
  CheckCircle2,
  Clock,
  DollarSign,
  RefreshCw,
  CircleDot,
  Award,
  Megaphone,
  Calendar as CalendarIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from '@/components/layout/list-page';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { cn } from '@/lib/utils';
import {
  useReferralReferralsAdmin,
  type ReferralAdminStatus,
  type AdminReferralRow,
} from '@/hooks/useReferralReferralsAdmin';
import { useReferralReferralsRealtime } from '@/hooks/useReferralReferralsRealtime';
import { useAuth } from '@/hooks/useAuth';
import { useReferralReferralActions } from '@/hooks/useReferralReferralActions';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import { useReferralCampaigns } from '@/hooks/useReferralCampaigns';
import { ReferralReferralsTableView } from '@/components/referral/ReferralReferralsTableView';
import { ReferralReferralDetailSheet } from '@/components/referral/ReferralReferralDetailSheet';
import { ReferralAffiliateSelector } from '@/components/referral/ReferralAffiliateSelector';
// Modal premium reaproveitado do CRM para exibir o negócio vinculado à indicação
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';

// ============================================================================
// CONFIG VISUAL (status)
// ============================================================================

const STATUS_LABEL: Record<ReferralAdminStatus, string> = {
  pending: 'Pendente',
  qualified: 'Qualificado',
  converted: 'Convertido',
  lost: 'Perdido',
};

const STATUS_DOT: Record<ReferralAdminStatus, string> = {
  pending: 'bg-yellow-500',
  qualified: 'bg-blue-500',
  converted: 'bg-green-500',
  lost: 'bg-red-500',
};

function formatBRL(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

// ============================================================================
// COMPONENTE
// ============================================================================

export default function ReferralReferralsAdmin() {
  // -------------------------------------------------------------------------
  // Filtros rápidos
  // -------------------------------------------------------------------------
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<ReferralAdminStatus | 'all'>('all');
  const [programId, setProgramId] = useState<string | 'all'>('all');
  const [campaignId, setCampaignId] = useState<string | 'all'>('all');

  // Filtros avançados (Sheet "Mais")
  const [affiliateId, setAffiliateId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  // Drawer de detalhes
  const [selected, setSelected] = useState<AdminReferralRow | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Modal premium do negócio vinculado (aberto pela coluna "Nº Negócio")
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);

  // Abre o modal premium do negócio vinculado à indicação
  const handleOpenDeal = (dealId: string) => {
    setSelectedDealId(dealId);
    setIsDealModalOpen(true);
  };

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // -------------------------------------------------------------------------
  // Dados de apoio
  // -------------------------------------------------------------------------
  const { data: programs = [] } = useReferralPrograms();
  const { data: campaigns = [] } = useReferralCampaigns(
    programId === 'all' ? null : programId,
  );

  // Debounce de busca (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Hook principal — listagem server-side
  const {
    referrals,
    totalCount,
    kpis,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useReferralReferralsAdmin(
    {
      search: debouncedSearch || null,
      status: status === 'all' ? null : status,
      program_id: programId === 'all' ? null : programId,
      campaign_id: campaignId === 'all' ? null : campaignId,
      affiliate_id: affiliateId || null,
      date_from: dateFrom || null,
      date_to: dateTo || null,
    },
    { page: currentPage, pageSize },
  );

  // Realtime — mantém a listagem sincronizada quando o negócio vinculado
  // muda de fase (ganho/perdido) ou quando indicações sofrem alterações,
  // dispensando F5 para refletir conversões e perdas.
  const { currentTenant } = useAuth();
  useReferralReferralsRealtime(currentTenant?.id);

  // Ações (reprocessar)
  const { reprocess, isReprocessing } = useReferralReferralActions();
  const [reprocessingId, setReprocessingId] = useState<string | null>(null);

  const handleReprocess = (id: string) => {
    setReprocessingId(id);
    reprocess(id, {
      onSettled: () => setReprocessingId(null),
    });
  };

  const { isRefreshing, triggerRefresh } = useRefreshWithTimestamp();
  const handleRefresh = () => {
    triggerRefresh(async () => {
      await refetch();
    });
  };

  // Reset de página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearch,
    status,
    programId,
    campaignId,
    affiliateId,
    dateFrom,
    dateTo,
  ]);

  // Reset campanha quando muda programa
  useEffect(() => {
    setCampaignId('all');
  }, [programId]);

  // Contador de filtros avançados ativos
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    if (affiliateId) count++;
    if (dateFrom || dateTo) count++;
    return count;
  }, [affiliateId, dateFrom, dateTo]);

  const clearAdvancedFilters = () => {
    setAffiliateId('');
    setDateFrom('');
    setDateTo('');
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <ListPageLayout>
      {/* Header */}
      <ListPageHeader
        title="Indicações"
        description="Acompanhe todos os leads capturados pelos seus afiliados, com vínculo automático ao CRM."
        actions={
          <Button
            variant="outline"
            size="icon-sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Atualizar dados"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')}
            />
          </Button>
        }
      />

      {/* KPIs */}
      {!isLoading && (
        <ListPageKpis>
          <KpiCard
            color="purple"
            icon={Inbox}
            label="Total"
            value={kpis.total}
          />
          <KpiCard
            color="yellow"
            icon={Clock}
            label="Pendentes"
            value={kpis.pending}
          />
          <KpiCard
            color="green"
            icon={CheckCircle2}
            label="Convertidas"
            value={`${kpis.converted} (${kpis.conversionRate.toFixed(1)}%)`}
          />
          <KpiCard
            color="blue"
            icon={DollarSign}
            label="Receita"
            value={formatBRL(kpis.totalRevenue)}
          />
        </ListPageKpis>
      )}

      {/* Filtros rápidos */}
      <ListPageFiltersBar
        onMoreClick={() => setIsMoreFiltersOpen(true)}
        moreCount={advancedFilterCount}
        isFetching={isFetching}
      >
        {/* Busca */}
        <div className="relative">
          <Search className="h-3 w-3 sm:h-3.5 sm:w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Buscar por nome, e-mail, telefone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn(
              LIST_FILTERS_INPUT_CLASS,
              'pl-7 w-[160px] sm:w-[260px]',
            )}
          />
        </div>

        {/* Status */}
        <Select
          value={status}
          onValueChange={(v) => setStatus(v as ReferralAdminStatus | 'all')}
        >
          <SelectTrigger
            className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[140px]')}
          >
            <CircleDot className="h-3 w-3 mr-1 inline-block" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {/* Mantém o nome do filtro visível no trigger (padrão TaskList) */}
            <SelectItem value="all">Status</SelectItem>
            {(Object.keys(STATUS_LABEL) as ReferralAdminStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                <span className="inline-flex items-center gap-2">
                  <span
                    className={cn(
                      'inline-block h-2 w-2 rounded-full',
                      STATUS_DOT[s],
                    )}
                  />
                  {STATUS_LABEL[s]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Programa */}
        <Select value={programId} onValueChange={setProgramId}>
          <SelectTrigger
            className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[160px]')}
          >
            <Award className="h-3 w-3 mr-1 inline-block" />
            <SelectValue placeholder="Programa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Programa</SelectItem>
            {programs.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Campanha (filtrada por programa) */}
        <Select value={campaignId} onValueChange={setCampaignId}>
          <SelectTrigger
            className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[160px]')}
          >
            <Megaphone className="h-3 w-3 mr-1 inline-block" />
            <SelectValue placeholder="Campanha" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Campanha</SelectItem>
            {campaigns.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      {/* Tabela */}
      <ListPageContent fullBleed>
        {error ? (
          <div className="p-6 text-sm text-destructive">
            Erro ao carregar indicações. Tente recarregar a página.
          </div>
        ) : (
          <ReferralReferralsTableView
            referrals={referrals}
            isLoading={isLoading}
            onOpenDetails={(id) => {
              const found = referrals.find((r) => r.id === id);
              if (found) {
                setSelected(found);
                setSheetOpen(true);
              }
            }}
            onReprocess={handleReprocess}
            reprocessingId={reprocessingId}
            onOpenDeal={handleOpenDeal}
          />
        )}
      </ListPageContent>

      {/* Footer */}
      <ListPageFooter>
        <PaginationFooter
          totalItems={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[10, 20, 50, 100]}
          borderClassName=""
          className="mt-0 pt-0"
        />
      </ListPageFooter>

      {/* Sheet de filtros avançados */}
      <Sheet open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 flex flex-col"
        >
          <SheetHeader className="p-4 border-b">
            <SheetTitle>Filtros avançados</SheetTitle>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="p-4 space-y-5">
              {/* Faixa de criação */}
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-1.5">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  Período de criação
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">De</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Até</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="h-9"
                    />
                  </div>
                </div>
              </div>

              {/* Afiliado — seletor com busca server-side (nome/email/telefone/CPF/CNPJ) */}
              <div className="space-y-2">
                <Label className="text-sm">Afiliado</Label>
                <ReferralAffiliateSelector
                  value={affiliateId || null}
                  onChange={(id) => setAffiliateId(id || '')}
                />
              </div>
            </div>
          </ScrollArea>

          <div className="p-4 border-t flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={clearAdvancedFilters}
            >
              Limpar
            </Button>
            <Button
              className="flex-1"
              onClick={() => setIsMoreFiltersOpen(false)}
            >
              Aplicar
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Detail Sheet — v2.0.0: agora carrega dados via RPC própria */}
      <ReferralReferralDetailSheet
        referralId={sheetOpen ? selected?.id ?? null : null}
        onClose={() => {
          setSheetOpen(false);
          setSelected(null);
        }}
        onReprocess={handleReprocess}
        isReprocessing={isReprocessing}
        onOpenDeal={handleOpenDeal}
      />

      {/* Modal premium do negócio vinculado (clique na coluna "Nº Negócio") */}
      <DealDetailsModalPremium
        dealId={selectedDealId}
        open={isDealModalOpen}
        onOpenChange={(open) => {
          setIsDealModalOpen(open);
          if (!open) setSelectedDealId(null);
        }}
      />
    </ListPageLayout>
  );
}
